import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { useSaveSettings } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

const remoteControlFormSchema = z.object({
  isRemoteControlEnabled: z.boolean(),
});

type RemoteControlFormValues = z.infer<typeof remoteControlFormSchema>;

export const RemoteControlForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    isRemoteControlEnabled:
      settings['ribbonPlugin.settings']?.remotecontrol_enabled ?? false,
  };

  const form = useForm<RemoteControlFormValues>({
    resolver: zodResolver(remoteControlFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: RemoteControlFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        remotecontrol_enabled: data.isRemoteControlEnabled,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          title: messages.genericToastTitleSuccess,
          description: messages.settingsGeneralSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          title: messages.genericToastTitleError,
          description: messages.settingsGeneralFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Remote control</h3>
            <div className="space-y-4">
              <FormField
                key="isRemoteControlEnabled"
                control={form.control}
                name="isRemoteControlEnabled"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">                        
                        {
                        messages.settingsGeneralRemoteControlEnabledMessagesLabel
                        }
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsGeneralRemoteControlEnabledMessagesDescription
                        }                        
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="remote-control-enabled-switch"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-general-remote-control-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
